import { useEffect, useRef, useState } from 'react';
import type { DeskSnapshot } from '../api/generated/types.gen';
import { num, stamp, tenths, toTenths } from '../format';
import { sideName } from '../market';
import { MAX_BOOK_AGE, type Side } from './Ticket';

const ROW = 21;
const MAX_ROWS = 10;

/** Kalshi books list bids only. Asks on one side are the opposite side's bids at 100¢ minus their price. */
export function Depth({ desk, side, mine }: { desk: DeskSnapshot; side: Side; mine?: { bids: ReadonlyMap<number, number>; asks: ReadonlyMap<number, number> } }) {
  // Show as many whole levels per side as the box holds (header and spread rows included), never a cut-off row.
  const box = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState(4);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setRows(Math.max(1, Math.min(MAX_ROWS, Math.floor((el.clientHeight - 8 - 2 * ROW) / (2 * ROW)))));
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const book = desk.book && desk.book.ticker === desk.selected?.ticker ? desk.book : null;
  const age = book ? Math.max(0, desk.server_time - book.observed_at) : null;
  // Levels are keyed in tenths of a cent: 15-minute tails trade in 0.1¢ steps.
  const level = ([p, q]: string[]) => ({ cents: toTenths(p)!, qty: Number(q) });
  const bids = book ? book[side].map(level).sort((a, b) => b.cents - a.cents).slice(0, rows) : [];
  const asks = book ? book[side === 'yes' ? 'no' : 'yes'].map(level).map(l => ({ cents: 1000 - l.cents, qty: l.qty })).sort((a, b) => a.cents - b.cents).slice(0, rows) : [];
  const max = Math.max(1, ...bids.map(b => b.qty), ...asks.map(a => a.qty));
  const bar = (q: number) => ({ width: `${q / max * 100}%` });
  return <section className="panel depth-panel" aria-label="Order book depth" data-testid="depth">
    <div className="panel-head"><h2>Depth · {sideName(side, desk.selected?.ticker)}</h2>{/* Plan 059: red from 5 s, when buys and estimates wait for a fresher book. */}
      <span className={`num${age != null && age >= MAX_BOOK_AGE ? ' down' : ''}`} data-testid="depth-age"
        title={age != null && age >= MAX_BOOK_AGE ? `Older than ${MAX_BOOK_AGE}s: buys and estimates wait for a fresher book` : undefined}>{book ? `${stamp(book.observed_at)} · ${Math.round(age!)}s old` : ''}</span></div>
    <div className="depth-scroll" ref={box} role="region" aria-label="Depth levels" data-testid="depth-scroll">
    {!book ? <p className="depth-empty">Waiting for this contract’s depth snapshot.</p> : <div className="depth">
      <div className="drow head"><div className="l">Bids</div><div className="p">¢</div><div className="r">Asks</div></div>
      {[...asks].reverse().map(a => <div className="drow" key={`a${a.cents}`}><div className="l"/><div className="p">{tenths(a.cents).replace('¢', '')}</div><div className="r"><i style={bar(a.qty)}/><b>{num(a.qty)}</b>{mine?.asks.get(a.cents) ? <span className="mine">you {num(mine.asks.get(a.cents))}</span> : null}</div></div>)}
      <div className="drow spread"><div className="l"/><div className="p">{bids[0] && asks[0] ? `spread ${tenths(asks[0].cents - bids[0].cents).replace('¢', '')}` : 'no spread'}</div><div className="r"/></div>
      {bids.map(b => <div className="drow" key={`b${b.cents}`}><div className="l">{mine?.bids.get(b.cents) ? <span className="mine">you {num(mine.bids.get(b.cents))}</span> : null}<i style={bar(b.qty)}/><b>{num(b.qty)}</b></div><div className="p">{tenths(b.cents).replace('¢', '')}</div><div className="r"/></div>)}
    </div>}
    </div>
  </section>;
}
